<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  activeFilters: { [key: string]: string };
}>();

const emit = defineEmits<{
  (e: 'update:filter', filterName: string, value: string): void;
}>();

const { t } = useI18n();

interface Suggestion {
  label: string;
  action: () => void;
}

const suggestions = computed<Suggestion[]>(() => {
  const result: Suggestion[] = [];

  // 1. Suggest increasing price if price filter is active
  if (props.activeFilters.price) {
    const [min, max] = props.activeFilters.price.split('-').map(Number);
    if (!isNaN(max)) {
      const newMax = Math.round(max * 2 / 100) * 100;
      result.push({
        label: t('components.catalog.suggestion.increase_max_price', { price: newMax }),
        action: () => {
          emit('update:filter', 'price', `${min}-${newMax}`);
        }
      });
    }
  }

  // 2. Suggest changing stone form to round if it's something else
  if (props.activeFilters.stone_form && props.activeFilters.stone_form !== 'round') {
    result.push({
      label: t('components.catalog.suggestion.try_round_stone_form'),
      action: () => emit('update:filter', 'stone_form', 'round')
    });
  }

  // 3. Suggest clearing a specific filter (e.g., the last one or just any)
  const filtersToClear = Object.keys(props.activeFilters).filter(f => f !== 'stone_type');
  if (filtersToClear.length > 0) {
    const filterToClear = filtersToClear[filtersToClear.length - 1];
    result.push({
      label: t('components.catalog.suggestion.remove_filter', { filter: t(`components.catalog.filter.${filterToClear}.label`) }),
      action: () => emit('update:filter', filterToClear, '')
    });
  }

  return result;
});
</script>

<template>
  <div class="flex flex-col justify-center items-center min-h-[200px] p-6">
    <div class="text-center text-gray-600 text-base mb-6">
      {{ $t('components.catalog.no_products_found') }}
    </div>

    <div v-if="suggestions.length > 0" class="flex flex-col items-center gap-4">
      <p class="text-sm text-gray-500 uppercase tracking-widest">{{ $t('components.catalog.suggestions_title') }}</p>
      <div class="flex flex-wrap justify-center gap-3">
        <button
            v-for="(suggestion, index) in suggestions"
            :key="index"
            @click="suggestion.action"
            class="px-4 py-2 bg-white border border-brand-main text-brand-text hover:bg-brand-main hover:text-white text-sm transition-colors rounded-sm uppercase tracking-wider"
        >
          {{ suggestion.label }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>